import { TRACE_COLORS } from "../constants/colors";

/**
 * Generates a consistent color for a given ID using a hash function.
 * The same ID will always return the same color.
 *
 * @param id - The ID to generate a color for (e.g., trace_id, span_id)
 * @returns A hex color string from the TRACE_COLORS palette
 */
export function getHashColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % TRACE_COLORS.length;
  return TRACE_COLORS[index]!;
}

/**
 * Generates a consistent color for a trace ID.
 *
 * @param traceId - The trace ID to generate a color for
 * @returns A hex color string from the TRACE_COLORS palette
 */
export function getTraceColor(traceId: string): string {
  return getHashColor(traceId);
}

/**
 * Generates a consistent color for a health check ID.
 *
 * @param healthCheckId - The health check ID to generate a color for
 * @returns A hex color string from the HEALTH_CHECK_COLORS palette
 */
export function getHealthCheckColor(healthCheckId: string): string {
  return getHashColor(healthCheckId);
}

/**
 * Generates a consistent color for a span ID.
 *
 * @param spanId - The span ID to generate a color for
 * @returns A hex color string from the TRACE_COLORS palette
 */
export function getSpanColor(spanId: string): string {
  return getHashColor(spanId);
}

export type SeverityChipColor = "default" | "info" | "warning" | "error";

export function getSeverityChipColor(severityNumber: number): SeverityChipColor {
  // OpenTelemetry severity_number ranges:
  // 1-4 TRACE, 5-8 DEBUG, 9-12 INFO, 13-16 WARN, 17-20 ERROR, 21-24 FATAL
  if (severityNumber >= 17) return "error";
  if (severityNumber >= 13) return "warning";
  if (severityNumber >= 9) return "info";
  return "default";
}
